import React, { FC, useEffect, useMemo, useRef, useState } from 'react'

import { AppId } from '@inno-tools/container-bridge'
import { ArrowRightIcon, IconWeight } from '@inno-ui/icons'
import clsx from 'clsx'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { Trans, useTranslation } from 'react-i18next'

import styles from './RoleOverviewScreen.module.scss'
import {
  RoleOverviewAppKey,
  RoleOverviewRole,
  RoleOverviewScreenProps
} from './RoleOverviewScreen.types'
import { combineRoleApps, getGreetingKey } from './RoleOverviewScreen.utils'

const APP_IDS: Record<RoleOverviewAppKey, AppId> = {
  project_management: AppId.PROJECT_MANAGEMENT,
  tasks: AppId.TASK_MANAGEMENT,
  time_tracking: AppId.TIMETRACKING,
  files: AppId.CLUSTER_FILES,
  compliance_center: AppId.SUBSIDIES,
  my_organization: AppId.MY_ORGANIZATION,
  accounting: AppId.ACCOUNTING,
  hr: AppId.HR
}

const formatRoleList = (
  roles: RoleOverviewRole[],
  getLabel: (role: RoleOverviewRole) => string,
  locale: string
): string => {
  const labels = roles.map(getLabel)
  const listLocale = locale.startsWith('en') ? 'en-GB' : locale

  return new Intl.ListFormat(listLocale, {
    style: 'long',
    type: 'conjunction'
  }).format(labels)
}

export const RoleOverviewScreen: FC<RoleOverviewScreenProps> = ({
  roles,
  userName,
  onComplete
}) => {
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'desktop.full_page_onboarding.role_overview'
  })
  const { apps: availableApps } = useAvailableApps()
  const apps = useMemo(
    () =>
      combineRoleApps(roles).filter(app =>
        Boolean(availableApps[APP_IDS[app.key]])
      ),
    [availableApps, roles]
  )
  const [expandedApp, setExpandedApp] = useState<RoleOverviewAppKey | null>(
    apps[0]?.key ?? null
  )
  const startButtonRef = useRef<HTMLButtonElement>(null)
  const prefersReducedMotion = useReducedMotion()

  useEffect(() => {
    setExpandedApp(apps[0]?.key ?? null)
  }, [apps])

  useEffect(() => {
    startButtonRef.current?.focus()
  }, [])

  const roleList = formatRoleList(
    roles,
    role => t(`roles.${role}`),
    i18n.resolvedLanguage ?? i18n.language
  )
  const greeting = t(`greeting.${getGreetingKey(new Date().getHours())}`, {
    name: userName
  })

  return (
    <section
      className={styles.roleOverviewScreen}
      role='dialog'
      aria-modal='true'
      aria-labelledby='role-overview-title'
      aria-describedby='role-overview-description'
    >
      <div className={styles.leftColumn}>
        <header className={styles.header}>
          <h1 id='role-overview-title' className={styles.title}>
            {greeting}
          </h1>
          <p className={styles.roleSummary}>{roleList}</p>
        </header>

        <div id='role-overview-description' className={styles.description}>
          <p>
            <strong>{t('platform_description_lead')}</strong>{' '}
            {t('platform_description')}
          </p>
          <p>
            <Trans
              t={t}
              i18nKey='role_description'
              values={{ roles: roleList }}
              components={{ strong: <strong /> }}
            />
          </p>
        </div>

        <button
          ref={startButtonRef}
          type='button'
          className={styles.startButton}
          onClick={onComplete}
        >
          <span>{t('start_button')}</span>
        </button>
      </div>

      <div className={styles.appList} aria-label={t('apps_label')}>
        {apps.map(app => {
          const presentation = availableApps[APP_IDS[app.key]]
          const isExpanded = expandedApp === app.key
          const panelId = `role-overview-app-${app.key}`

          if (!presentation) return null

          return (
            <motion.article
              key={app.key}
              layout
              className={clsx(styles.appCard, {
                [styles.expanded]: isExpanded
              })}
              transition={{
                duration: prefersReducedMotion ? 0 : 0.24,
                ease: [0.22, 1, 0.36, 1]
              }}
            >
              <button
                type='button'
                className={styles.appHeader}
                aria-expanded={isExpanded}
                aria-controls={panelId}
                onClick={() =>
                  setExpandedApp(currentApp =>
                    currentApp === app.key ? null : app.key
                  )
                }
              >
                <img
                  src={presentation.icon}
                  alt=''
                  className={styles.appIcon}
                />
                <span className={styles.appHeading}>
                  <span className={styles.appTitle}>{presentation.name}</span>
                  {!isExpanded && (
                    <span className={styles.shortDescription}>
                      {t(`apps.${app.key}.short_description`)}
                    </span>
                  )}
                </span>
              </button>

              <AnimatePresence initial={false}>
                {isExpanded && (
                  <motion.div
                    id={panelId}
                    className={styles.appDetailsReveal}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      duration: prefersReducedMotion ? 0 : 0.24,
                      ease: [0.22, 1, 0.36, 1]
                    }}
                  >
                    <div className={styles.appDetails}>
                      <p>{t(`apps.${app.key}.long_description`)}</p>
                      <ul>
                        {app.items.map(item => (
                          <li
                            key={`${item.category ?? 'item'}-${item.translationKey}`}
                          >
                            <ArrowRightIcon
                              color='#FFFFFF'
                              weight={IconWeight.Bold}
                              aria-hidden='true'
                            />
                            <span>{t(`items.${item.translationKey}`)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.article>
          )
        })}
      </div>
    </section>
  )
}
